import type { TFunction } from 'i18next';
import { ShipmentType } from 'constants/shipmentTypes.constants';
import {
  Configs,
  DisplayIdReferencesBox,
  YourOtherParcelsBox,
} from 'interfaces/WidgetConfigs.interface';
import PmParcel from 'interfaces/PmParcel';
import { parseTemplate } from './template';

export enum ShipmentDisplay {
  orderSummary = 'orderSummary',
  twoParcels = 'twoParcels',
  SingleShipmentOrder = 'singleShipmentOrder',
  MultiShipmentOrder = 'multiShipmentOrder',
  dropdownLabel = 'dropdownLabel',
  totalParcels = 'totalParcels',
}

const getDisplayConfigByShipmentType = (
  configs: Configs,
  shipmentType?: ShipmentType,
): YourOtherParcelsBox & DisplayIdReferencesBox => {
  if (shipmentType === ShipmentType.Return) {
    return {
      ...configs.return?.yourOtherParcelsBox,
      ...configs.return?.displayIdReferencesBox,
    };
  }

  return {
    ...configs.yourOtherParcelsBox,
    ...configs.displayIdReferencesBox,
  };
};

const getDefaultShipmentDisplay = (
  t: TFunction,
  shipmentType?: ShipmentType,
) => {
  const ORDER = t('ORDER');
  const TRACKING_NUMBER = t('TRACKING_NUMBER');
  const RETURN_ID = t('RETURN_ID');
  const SHIPMENT_ID = t('SHIPMENT_ID');

  const OTHER_PARCELS_NUMBER = t('OTHER_PARCELS_NUMBER', {
    interpolation: { skipOnVariables: true },
  });

  const NUMBER_OF_PARCELS = t('NUMBER_OF_PARCELS', {
    interpolation: { skipOnVariables: true },
  });

  const defaultDisplayShipment = {
    [ShipmentDisplay.dropdownLabel]: OTHER_PARCELS_NUMBER,
  };

  if (shipmentType === ShipmentType.Return) {
    return {
      ...defaultDisplayShipment,
      [ShipmentDisplay.orderSummary]: NUMBER_OF_PARCELS,
      [ShipmentDisplay.twoParcels]: `{% if shipment.return_id %}{{shipment.return_id}}{% else %}{{shipment.shipment_id}}{% endif %}`,
      [ShipmentDisplay.SingleShipmentOrder]: `{% if shipment.return_id %}${RETURN_ID}: {{shipment.return_id}}{% endif %}{%if shipment.order_reference %} (${ORDER}: {{shipment.order_reference}}){% endif %}`,
      [ShipmentDisplay.MultiShipmentOrder]: `{% if shipment.return_id %}${RETURN_ID}: {{shipment.return_id}}{% else %}${SHIPMENT_ID}: {{shipment.shipment_id}}{% endif %}`,
    };
  }

  return {
    ...defaultDisplayShipment,
    [ShipmentDisplay.orderSummary]: NUMBER_OF_PARCELS,
    [ShipmentDisplay.twoParcels]: `{% if shipment.tracking_number %}{{shipment.tracking_number}}{% else %}{{shipment.shipment_id}}{% endif %}`,
    [ShipmentDisplay.SingleShipmentOrder]: `{% if shipment.tracking_number %}${TRACKING_NUMBER}: {{shipment.tracking_number}}{% endif %}{%if shipment.order_reference %} (${ORDER}: {{shipment.order_reference}}){% endif %}`,
    [ShipmentDisplay.MultiShipmentOrder]: `{%if shipment.tracking_number%}${TRACKING_NUMBER}: {{shipment.tracking_number}}{% endif %}`,
  };
};

export const getShipmentDisplayFactory = (
  configs: Configs,
  shipment: PmParcel,
  t: TFunction,
) => {
  const displayConfigs = getDisplayConfigByShipmentType(
    configs,
    shipment.typeOfShipment,
  );

  const defaultDisplayShipment = getDefaultShipmentDisplay(
    t,
    shipment.typeOfShipment,
  );

  const shipmentMapObject = {
    shipment: {
      shipment_id: shipment.shipmentId,
      order_id: shipment.orderId,
      order_reference: shipment.orderCode,
      tracking_number: shipment.parcelId,
      shipment_reference: shipment.parcelAlias,
      additional_reference: shipment.parcelAlias2,
      return_id: shipment.returnTicket?.returnId,
      linked_shipment: {
        tracking_number: shipment?.additionalData?.linkedShipmentTrackingNumber,
      },
    },
  };

  return (
    key: ShipmentDisplay,
    additionalValues: Record<string, unknown> = {},
  ) => {
    const str = displayConfigs[key] || defaultDisplayShipment[key];
    if (!str) return '';

    return parseTemplate(str, { ...shipmentMapObject, ...additionalValues });
  };
};

export const isReturnShipment = (shipment: PmParcel): boolean =>
  shipment?.typeOfShipment === ShipmentType.Return;
